/*
 * Custom sign calculator — Tailor Made Rooms
 *
 * Type a size, get the price on all three materials, pick one, pay two ways.
 *
 * Reads two theme assets:
 *   sign-pricing.json    retail prices, generated from cost. Source of truth for display.
 *   sign-variants.json   "36x8|metal|1" -> variant id. Generated by the sync script.
 *
 * Rules that are not negotiable:
 *   - Never interpolate a price. If a size isn't a row in sign-pricing.json, there is no price.
 *   - null means the material is not offered. Never render 0, never substitute another material.
 *   - Two-sided is derived at display time from the multiplier. Never stored.
 *   - No variant id means no Pay in full button. Deposit path only.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-tmr-calculator]');
  if (!root) return;

  var CFG = {
    pricingUrl: root.dataset.pricingUrl,
    variantsUrl: root.dataset.variantsUrl,
    depositVariant: root.dataset.depositVariant,
    deposit: parseFloat(root.dataset.depositAmount) || 75,
    twoSided: parseFloat(root.dataset.twoSidedMultiplier) || 1.5,
    quoteUrl: root.dataset.quoteUrl || '/pages/contact'
  };

  var MATERIALS = [
    { key: 'canvas',  name: 'Canvas',  use: 'Indoor only',      blurb: '1.5″ gallery wrap, hand stretched' },
    { key: 'metal',   name: 'Metal',   use: 'Indoor & outdoor', blurb: '6 mm DIBOND, standoffs included' },
    { key: 'acrylic', name: 'Acrylic', use: 'Indoor only',      blurb: '1/8″ acrylic, float mount or standoffs' }
  ];

  var $w = root.querySelector('[data-tmr-w]');
  var $h = root.querySelector('[data-tmr-h]');
  var $result = root.querySelector('[data-tmr-result]');
  var $flip = root.querySelector('[data-tmr-flip]');
  var $sides = root.querySelectorAll('[data-tmr-sides]');

  var INDEX = {};   // "36x8" -> {w,h,canvas,metal,acrylic,conflict}
  var LIST = [];
  var VARIANTS = {};
  var ready = false;

  var state = {
    w: parseInt($w.value, 10) || 0,
    h: parseInt($h.value, 10) || 0,
    sides: 1,
    material: null,
    busy: false
  };

  // ---------------------------------------------------------------- data

  Promise.all([
    fetch(CFG.pricingUrl).then(function (r) { return r.json(); }),
    fetch(CFG.variantsUrl).then(function (r) { return r.json(); }).catch(function () { return {}; })
  ]).then(function (res) {
    buildIndex(res[0]);
    VARIANTS = (res[1] && res[1].variants) ? res[1].variants : (res[1] || {});
    ready = true;
    render();
  }).catch(function () {
    $result.innerHTML = '<div class="tmr-note tmr-note-quiet">' +
      'Prices are loading — if this doesn\'t clear, call us on ' +
      '<a href="tel:16168972070">1‑616‑897‑2070</a> and we\'ll price it for you.</div>';
  });

  function buildIndex(data) {
    if (data && data.meta && data.meta.rules && data.meta.rules.two_sided_multiplier) {
      CFG.twoSided = data.meta.rules.two_sided_multiplier;
    }
    (data.shapes || []).forEach(function (shape) {
      (shape.sizes || []).forEach(function (row) {
        var parts = String(row.size).split('x');
        var w = parseInt(parts[0], 10), h = parseInt(parts[1], 10);
        var key = w + 'x' + h;
        if (!INDEX[key]) {
          INDEX[key] = { w: w, h: h, key: key, canvas: null, metal: null, acrylic: null, conflict: false };
          LIST.push(INDEX[key]);
        }
        var e = INDEX[key];
        ['canvas', 'metal', 'acrylic'].forEach(function (m) {
          var v = row[m];
          if (v == null) return;
          if (e[m] == null) { e[m] = v; return; }
          if (e[m] !== v) { e.conflict = true; e[m] = Math.min(e[m], v); }
        });
      });
    });
    // the size table sits outside the buy box, so look at the whole document
    if (document.querySelector('[data-tmr-all-sizes]')) renderSizeTable();
  }

  // ---------------------------------------------------------------- helpers

  function money(n) {
    var s = Math.round(n * 100) / 100;
    var str = s % 1 === 0 ? s.toFixed(0) : s.toFixed(2);
    return '$' + str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function priceFor(row, mat) {
    var base = row[mat];
    if (base == null) return null;
    return state.sides === 2 ? base * CFG.twoSided : base;
  }
  function variantFor(row, mat) {
    return VARIANTS[row.key + '|' + mat + '|' + state.sides] || null;
  }
  function lowest(row) {
    var v = [row.canvas, row.metal, row.acrylic].filter(function (x) { return x != null; });
    return v.length ? Math.min.apply(null, v) : null;
  }
  function nearest(w, h, n) {
    return LIST.slice().map(function (r) {
      return { row: r, d: Math.min(Math.abs(r.w - w) + Math.abs(r.h - h),
                                   Math.abs(r.w - h) + Math.abs(r.h - w)) };
    }).sort(function (a, b) { return a.d - b.d; })
      .slice(0, n)
      .sort(function (a, b) { return (a.row.w * a.row.h) - (b.row.w * b.row.h); })
      .map(function (x) { return x.row; });
  }

  // ---------------------------------------------------------------- events

  $w.addEventListener('input', function () { state.w = parseInt($w.value, 10) || 0; state.material = null; render(); });
  $h.addEventListener('input', function () { state.h = parseInt($h.value, 10) || 0; state.material = null; render(); });
  $flip.addEventListener('click', flip);
  Array.prototype.forEach.call($sides, function (b) {
    b.addEventListener('click', function () {
      state.sides = parseInt(b.dataset.tmrSides, 10);
      render();
    });
  });

  function flip() {
    var t = state.w; state.w = state.h; state.h = t;
    $w.value = state.w; $h.value = state.h;
    state.material = null;
    render();
  }

  // ---------------------------------------------------------------- render

  function render() {
    if (!ready) return;

    Array.prototype.forEach.call($sides, function (b) {
      b.setAttribute('aria-pressed', parseInt(b.dataset.tmrSides, 10) === state.sides ? 'true' : 'false');
    });

    if (!state.w || !state.h) {
      $result.innerHTML = '<div class="tmr-note tmr-note-quiet">Enter a width and a height to see prices on all three materials.</div>';
      return;
    }

    var row = INDEX[state.w + 'x' + state.h];
    if (row) { renderMatch(row); return; }
    renderNoMatch();
  }

  function renderMatch(row) {
    var html = '<div class="tmr-matlist" role="group" aria-label="Choose a material">';

    MATERIALS.forEach(function (m) {
      var p = priceFor(row, m.key);
      if (p == null) {
        html += '<button class="tmr-mat" type="button" disabled>' +
          '<span><span class="tmr-mat-name">' + m.name + '</span>' +
          '<span class="tmr-mat-use">Not offered at this size</span></span>' +
          '<span class="tmr-mat-na">—</span></button>';
      } else {
        html += '<button class="tmr-mat" type="button" data-mat="' + m.key + '" ' +
          'aria-pressed="' + (state.material === m.key) + '">' +
          '<span><span class="tmr-mat-name">' + m.name + '</span>' +
          '<span class="tmr-mat-use">' + esc(m.use) + ' · ' + esc(m.blurb) + '</span></span>' +
          '<span class="tmr-mat-price">' + money(p) + '</span></button>';
      }
    });
    html += '</div>';

    if (row.conflict) {
      html += '<div class="tmr-review">Two entries on our price sheet disagree on this size. ' +
        'We\'re showing the lower price and will confirm it with you before we take payment.</div>';
    }

    if (!state.material) {
      html += '<p class="tmr-pick-hint">Pick a material to see how you\'d like to pay.</p>';
    } else {
      html += renderPaths(row);
    }

    $result.innerHTML = html;
    wireMaterials(row);
    wirePaths(row);
  }

  function renderPaths(row) {
    var full = priceFor(row, state.material);
    var balance = Math.max(0, full - CFG.deposit);
    var matName = MATERIALS.filter(function (m) { return m.key === state.material; })[0].name;
    var variantId = variantFor(row, state.material);

    var html = '<div class="tmr-paths">';
    html += '<div class="tmr-chosen"><span>' + matName + ' · ' + row.w + ' × ' + row.h + '″ · ' +
            (state.sides === 2 ? 'two sided' : 'one sided') + '</span><b>' + money(full) + '</b></div>';

    if (variantId && !row.conflict) {
      html += '<div class="tmr-path tmr-path-primary">' +
        '<h4>Pay in full</h4>' +
        '<span class="tmr-amt">' + money(full) + '</span>' +
        '<button class="tmr-btn tmr-btn-solid" type="button" data-buy="' + variantId + '">' +
          'Add to cart — ' + money(full) + '</button>' +
        '<p class="tmr-fine">You still see and approve your proof before anything is printed. ' +
        'Four rounds of changes are included, and shipping is free.</p>' +
        '</div>';
    }

    html += '<div class="tmr-path">' +
      '<h4>' + (variantId && !row.conflict
        ? 'Rather see the design first? Pay in two parts.'
        : 'Start your sign') + '</h4>' +
      '<div class="tmr-split"><b>' + money(CFG.deposit) + '</b> now, then <b>' + money(balance) +
      '</b> when you approve the design</div>' +
      '<button class="tmr-btn tmr-btn-ghost" type="button" data-deposit="1">' +
        'Start with a ' + money(CFG.deposit) + ' design deposit</button>' +
      '<p class="tmr-fine">Same total — ' + money(full) + '. We design your sign, you see it, and we ' +
      'invoice the balance only once you\'re happy. The ' + money(CFG.deposit) + ' is non‑refundable ' +
      'if you decide not to go ahead after we\'ve designed it.</p>' +
      '</div>';

    html += '</div>';
    return html;
  }

  function renderNoMatch() {
    var w = state.w, h = state.h;
    var flipped = INDEX[h + 'x' + w];
    var html = '';

    if (flipped) {
      html += '<div class="tmr-note tmr-note-flip"><strong>' + w + ' × ' + h +
        '″ isn\'t on our price list, but ' + h + ' × ' + w + '″ is.</strong>' +
        '<div style="margin-top:8px"><button class="tmr-chip" type="button" data-flip="1">⇆ Use ' +
        h + ' × ' + w + '″ — from ' + money(lowest(flipped)) + '</button></div></div>';
    } else {
      html += '<div class="tmr-note tmr-note-quiet"><strong>We don\'t price ' + w + ' × ' + h +
        '″ off the shelf.</strong> Here are the closest sizes we do — or we\'ll quote yours exactly.</div>';
    }

    html += '<div class="tmr-chips">';
    nearest(w, h, 8).forEach(function (r) {
      var only = (r.canvas == null && r.acrylic == null && r.metal != null) ? ' metal only' : '';
      html += '<button class="tmr-chip" type="button" data-size="' + r.key + '">' +
        r.w + ' × ' + r.h + '″ <span>from ' + money(lowest(r)) + only + '</span></button>';
    });
    html += '</div>';

    var q = CFG.quoteUrl + (CFG.quoteUrl.indexOf('?') > -1 ? '&' : '?') +
            'size=' + w + 'x' + h + '&sides=' + state.sides;

    html += '<div class="tmr-path" style="margin-top:16px">' +
      '<h4>Want ' + w + ' × ' + h + '″ exactly?</h4>' +
      '<p class="tmr-fine" style="margin-top:6px">We cut custom sizes. Send us the size and roughly ' +
      'what it says and we\'ll price it — usually same day.</p>' +
      '<a class="tmr-btn tmr-btn-ghost" href="' + esc(q) + '">Request a quote for ' + w + ' × ' + h + '″</a>' +
      '</div>';

    $result.innerHTML = html;
    wireChips();
  }

  // ---------------------------------------------------------------- wiring

  function wireMaterials(row) {
    Array.prototype.forEach.call($result.querySelectorAll('[data-mat]'), function (b) {
      b.addEventListener('click', function () {
        var k = b.getAttribute('data-mat');
        state.material = (state.material === k) ? null : k;
        render();
      });
    });
  }

  function wireChips() {
    Array.prototype.forEach.call($result.querySelectorAll('[data-size]'), function (b) {
      b.addEventListener('click', function () {
        var k = b.getAttribute('data-size').split('x');
        state.w = parseInt(k[0], 10); state.h = parseInt(k[1], 10); state.material = null;
        $w.value = state.w; $h.value = state.h;
        render();
      });
    });
    Array.prototype.forEach.call($result.querySelectorAll('[data-flip]'), function (b) {
      b.addEventListener('click', flip);
    });
  }

  function wirePaths(row) {
    var buy = $result.querySelector('[data-buy]');
    if (buy) {
      buy.addEventListener('click', function () {
        addToCart(buy.getAttribute('data-buy'), {
          'Design brief': 'Sent by email after checkout'
        }, buy);
      });
    }
    var dep = $result.querySelector('[data-deposit]');
    if (dep) {
      dep.addEventListener('click', function () {
        var full = priceFor(row, state.material);
        addToCart(CFG.depositVariant, {
          'Size': row.w + ' × ' + row.h + '″',
          'Material': MATERIALS.filter(function (m) { return m.key === state.material; })[0].name,
          'Sides': state.sides === 2 ? 'Two sided' : 'One sided',
          'Quoted total': money(full),
          'Balance due after approval': money(Math.max(0, full - CFG.deposit))
        }, dep);
      });
    }
  }

  function addToCart(variantId, properties, btn) {
    if (state.busy) return;
    state.busy = true;
    var label = btn.textContent;
    btn.textContent = 'Adding…';
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      window.location.href = '/cart';
    }).catch(function () {
      state.busy = false;
      btn.disabled = false;
      btn.textContent = label;
      var err = document.createElement('p');
      err.className = 'tmr-fine tmr-error';
      err.textContent = 'That didn\'t go through. Try again, or call us on 1‑616‑897‑2070.';
      btn.parentNode.appendChild(err);
    });
  }

  // ---------------------------------------------------------------- size table

  function renderSizeTable() {
    var tb = document.querySelector('[data-tmr-all-sizes]');
    if (!tb) return;
    var rows = LIST.slice().sort(function (a, b) { return (a.w * a.h) - (b.w * b.h); });
    tb.innerHTML = rows.map(function (r) {
      function cell(v) { return v == null ? '<td class="tmr-na">not offered</td>' : '<td>' + money(v) + '</td>'; }
      return '<tr><td>' + r.w + ' × ' + r.h + '</td>' + cell(r.canvas) + cell(r.metal) + cell(r.acrylic) + '</tr>';
    }).join('');
  }

})();
